import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Building2,
  Truck,
  Users,
  UserPlus,
  Calendar,
  PartyPopper,
  Plus,
  Trash2,
  Sun,
  Moon,
  HardHat,
  Info,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Company types with their asset type options
const COMPANY_TYPES = {
  crane: {
    name: 'Crane Hire',
    assetTypes: [
      'Franna 10t', 'Franna 15t', 'Franna 20t', 'Franna 25t',
      'All Terrain 25t', 'All Terrain 40t', 'All Terrain 55t', 'All Terrain 80t', 'All Terrain 100t'
    ]
  },
  earthmoving: {
    name: 'Earthmoving',
    assetTypes: [
      'Mini Excavator 1.5t', 'Mini Excavator 3t', 'Excavator 8t', 'Excavator 13t', 'Excavator 20t',
      'Skid Steer Loader', 'Wheel Loader', 'Bulldozer D6', 'Grader'
    ]
  },
  concrete_pumping: {
    name: 'Concrete Pumping',
    assetTypes: [
      'Line Pump', 'Boom Pump 24m', 'Boom Pump 28m', 'Boom Pump 32m', 'Boom Pump 36m',
      'Boom Pump 42m', 'Boom Pump 52m', 'Concrete Mixer', 'Agitator'
    ]
  },
  haulage: {
    name: 'Haulage',
    assetTypes: [
      'Prime Mover', 'Rigid Truck', 'Semi-Trailer', 'B-Double', 'Road Train',
      'Flat Top', 'Drop Deck', 'Low Loader', 'Side Tipper', 'Tanker',
      'Curtainsider', 'Tilt Tray'
    ]
  },
  street_sweeping: {
    name: 'Street Sweeping',
    assetTypes: [
      'Compact Sweeper', 'Truck-Mounted Sweeper', 'Regenerative Air Sweeper',
      'Mechanical Broom Sweeper', 'Vacuum Sweeper', 'Ride-On Sweeper'
    ]
  }
};

// Timezone options
const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
];

const STEPS = [
  { id: 2, title: 'Your Assets', icon: Truck },
  { id: 3, title: 'Your Operators', icon: Users },
  { id: 4, title: 'Your Customers', icon: UserPlus },
  { id: 5, title: 'Create your first job', icon: Calendar },
  { id: 6, title: 'Congratulations!', icon: PartyPopper }
];

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [currentStep, setCurrentStep] = useState(2);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Step 1: Company info - initialize theme from context
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    timezone: 'Australia/Sydney',
    theme: localStorage.getItem('theme') || 'light',
    companyType: ''
  });
  
  // Sync companyInfo.theme with context theme on mount
  useEffect(() => {
    setCompanyInfo(prev => ({ ...prev, theme: theme }));
  }, []);
  
  // Step 2: Assets
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
  const [customAssetTypes, setCustomAssetTypes] = useState([]);  // User-added custom types
  const [systemAssetTypes, setSystemAssetTypes] = useState([]);  // Tenant's seeded asset types (from data config)
  const [newCustomType, setNewCustomType] = useState('');  // Input for new custom type
  const [assets, setAssets] = useState([{ assetType: '', assetId: '' }]);
  
  // Step 3: Operators
  const [operators, setOperators] = useState([{ name: '' }]);
  
  // Step 4: Customers
  const [customers, setCustomers] = useState([{ name: '' }]);
  
  // Step 5: First job
  const [firstJob, setFirstJob] = useState({
    customerId: '',
    equipmentType: '',
    location: '',
    date: new Date().toISOString().split('T')[0],
    time: '07:00',
    notes: ''
  });
  
  // Created data IDs (from API responses)
  const [createdData, setCreatedData] = useState({
    customers: [],
    assets: [],
    operators: []
  });
  
  // Default supplier ID (created during signup)
  const [defaultSupplierId, setDefaultSupplierId] = useState(null);

  useEffect(() => {
    // Check if user should be here
    const showOnboarding = localStorage.getItem('show_onboarding_wizard');
    if (!showOnboarding && user?.onboarding?.wizard_completed) {
      navigate('/dashboard');
    }
    
    // Fetch default supplier
    const fetchDefaultSupplier = async () => {
      try {
        const response = await api.get('/suppliers');
        const suppliers = response.data;
        const defaultSupplier = suppliers.find(s => s.is_default) || suppliers[0];
        if (defaultSupplier) {
          setDefaultSupplierId(defaultSupplier.id);
        }
      } catch (e) {
        console.error('Failed to fetch suppliers:', e);
      }
    };
    fetchDefaultSupplier();

    // Pre-fill company name + industry captured during signup
    const fetchTenant = async () => {
      try {
        const response = await api.get('/tenants/current');
        const tenant = response.data || {};
        setCompanyInfo(prev => ({
          ...prev,
          companyName: prev.companyName || tenant.company_name || '',
          companyType: prev.companyType || tenant.industry_type || '',
        }));
      } catch (e) {
        console.error('Failed to fetch tenant:', e);
      }
    };
    fetchTenant();

    // Load the tenant's seeded asset types (Data Management → Asset Types)
    const fetchAssetTypes = async () => {
      try {
        const response = await api.get('/equipment-types');
        setSystemAssetTypes(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        console.error('Failed to fetch asset types:', e);
      }
    };
    fetchAssetTypes();
  }, [user, navigate, api]);

  const handleCompanyTypeChange = (type) => {
    setCompanyInfo(prev => ({ ...prev, companyType: type }));
    setSelectedAssetTypes([]); // Reset selected asset types
    setAssets([{ assetType: '', assetId: '' }]); // Reset assets
  };

  const handleAssetTypeToggle = (assetType) => {
    setSelectedAssetTypes(prev => 
      prev.includes(assetType) 
        ? prev.filter(t => t !== assetType)
        : [...prev, assetType]
    );
  };

  const addCustomAssetType = async () => {
    const trimmedType = newCustomType.trim();
    if (!trimmedType) return;

    // Already known locally — just select it
    if (systemAssetTypes.includes(trimmedType) || customAssetTypes.includes(trimmedType)) {
      setSelectedAssetTypes(prev => prev.includes(trimmedType) ? prev : [...prev, trimmedType]);
      setNewCustomType('');
      return;
    }

    try {
      // Persist the custom type to the tenant's data config (Asset Types)
      await api.post('/equipment-types', { name: trimmedType });
      setCustomAssetTypes(prev => [...prev, trimmedType]);
      setSelectedAssetTypes(prev => [...prev, trimmedType]);
      setNewCustomType('');
      toast.success(`"${trimmedType}" added to your asset types`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && /already exists/i.test(detail || '')) {
        // Exists in config but not in the loaded list — add locally and select
        setCustomAssetTypes(prev => [...prev, trimmedType]);
        setSelectedAssetTypes(prev => [...prev, trimmedType]);
        setNewCustomType('');
      } else {
        toast.error(detail || 'Failed to add asset type. Please try again.');
      }
    }
  };

  const removeCustomAssetType = (typeToRemove) => {
    setCustomAssetTypes(prev => prev.filter(t => t !== typeToRemove));
    setSelectedAssetTypes(prev => prev.filter(t => t !== typeToRemove));
    // Also remove any assets using this type
    setAssets(prev => prev.map(asset => 
      asset.assetType === typeToRemove ? { ...asset, assetType: '' } : asset
    ));
  };

  const addAsset = () => {
    setAssets(prev => [...prev, { assetType: '', assetId: '' }]);
  };

  const removeAsset = (index) => {
    if (assets.length > 1) {
      setAssets(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateAsset = (index, field, value) => {
    setAssets(prev => prev.map((asset, i) => 
      i === index ? { ...asset, [field]: value } : asset
    ));
  };

  const addOperator = () => {
    setOperators(prev => [...prev, { name: '' }]);
  };

  const removeOperator = (index) => {
    if (operators.length > 1) {
      setOperators(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateOperator = (index, value) => {
    setOperators(prev => prev.map((op, i) => 
      i === index ? { name: value } : op
    ));
  };

  const addCustomer = () => {
    setCustomers(prev => [...prev, { name: '' }]);
  };

  const removeCustomer = (index) => {
    if (customers.length > 1) {
      setCustomers(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateCustomer = (index, value) => {
    setCustomers(prev => prev.map((cust, i) => 
      i === index ? { name: value } : cust
    ));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return companyInfo.companyName.trim() && companyInfo.companyType;
      case 2:
        return assets.some(a => a.assetType && a.assetId.trim());
      case 3:
        return operators.some(o => o.name.trim());
      case 4:
        return customers.some(c => c.name.trim());
      case 5:
        // All required fields: Customer, Location, Equipment Type, Start Date, Start Time, Description
        return firstJob.customerId && 
               firstJob.equipmentType && 
               firstJob.location.trim() && 
               firstJob.date && 
               firstJob.time && 
               (firstJob.notes && firstJob.notes.trim());
      default:
        return true;
    }
  };

  const saveStep = async (step) => {
    setSaving(true);
    try {
      switch (step) {
        case 1:
          // Save company info (client_id remains unchanged from signup)
          await api.patch('/tenants/current', {
            company_name: companyInfo.companyName,
            timezone: companyInfo.timezone,
            industry_type: companyInfo.companyType
          });
          
          // Apply theme preference immediately
          setTheme(companyInfo.theme);
          localStorage.setItem('theme', companyInfo.theme);
          
          // Refresh user data so company name updates in header
          await refreshUser();
          break;
          
        case 2:
          // Create assets (idempotent — skip asset_ids that already exist)
          const createdAssets = [];
          let existingAssetIds = new Set();
          try {
            const existingEquip = await api.get('/equipment');
            existingAssetIds = new Set(
              (existingEquip.data || []).map(eq => (eq.asset_id || eq.name || '').trim().toLowerCase())
            );
          } catch (e) { /* fall back to creating */ }
          for (const asset of assets.filter(a => a.assetType && a.assetId.trim())) {
            const key = asset.assetId.trim().toLowerCase();
            if (existingAssetIds.has(key)) continue; // already created on a previous attempt
            try {
              const response = await api.post('/equipment', {
                name: asset.assetId,
                asset_id: asset.assetId,
                supplier_id: defaultSupplierId || 'internal',
                equipment_type: asset.assetType,
                status: 'available'
              });
              existingAssetIds.add(key);
              createdAssets.push(response.data);
            } catch (e) {
              console.error('Failed to create asset:', e);
            }
          }
          setCreatedData(prev => ({ ...prev, assets: createdAssets }));
          break;
          
        case 3:
          // Create operators (idempotent — skip names that already exist)
          const createdOperators = [];
          let existingOperatorNames = new Set();
          try {
            const existingOps = await api.get('/operators');
            existingOperatorNames = new Set(
              (existingOps.data || []).map(o => (o.name || '').trim().toLowerCase())
            );
          } catch (e) { /* fall back to creating */ }
          for (const op of operators.filter(o => o.name.trim())) {
            const key = op.name.trim().toLowerCase();
            if (existingOperatorNames.has(key)) continue;
            try {
              const response = await api.post('/operators', {
                name: op.name.trim(),
                supplier_id: defaultSupplierId || 'internal',
                role: 'operator',
                is_available: true
              });
              existingOperatorNames.add(key);
              createdOperators.push(response.data);
            } catch (e) {
              console.error('Failed to create operator:', e);
              // Check if it's a tier limit error
              if (e.response?.status === 403 && e.response?.headers?.['x-limit-reached']) {
                toast.error('Operator limit reached for your plan. Upgrade to add more operators.');
              }
            }
          }
          setCreatedData(prev => ({ ...prev, operators: createdOperators }));
          break;
          
        case 4:
          // Create customers (idempotent — skip names that already exist).
          // Prevents duplicate customers when an onboarding attempt is retried.
          const createdCustomers = [];
          let existingCustomerNames = new Set();
          try {
            const existingCusts = await api.get('/customers');
            existingCustomerNames = new Set(
              (existingCusts.data || []).map(c => (c.company_name || '').trim().toLowerCase())
            );
          } catch (e) { /* fall back to creating */ }
          for (const cust of customers.filter(c => c.name.trim())) {
            const key = cust.name.trim().toLowerCase();
            if (existingCustomerNames.has(key)) continue;
            try {
              const response = await api.post('/customers', {
                company_name: cust.name,
                contact_name: cust.name
              });
              existingCustomerNames.add(key);
              createdCustomers.push(response.data);
            } catch (e) {
              console.error('Failed to create customer:', e);
            }
          }
          setCreatedData(prev => ({ ...prev, customers: createdCustomers }));
          break;
          
        case 5:
          // Create first job
          await api.post('/jobs', {
            customer_id: firstJob.customerId,
            equipment_type: firstJob.equipmentType,
            // Use structured address if available from Google Places, otherwise use raw input
            one_off_street: firstJob.locationStreet || firstJob.location,
            one_off_suburb: firstJob.locationSuburb || '',
            one_off_state: firstJob.locationState || '',
            one_off_postcode: firstJob.locationPostcode || '',
            start_date: firstJob.date,
            start_time: firstJob.time,
            description: firstJob.notes || 'First job created during onboarding',
            notes: firstJob.notes,
            status: 'not_allocated'
          });
          break;
      }
      
      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Failed to save. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const saved = await saveStep(currentStep);
    if (saved) {
      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Mark onboarding as complete (retry once on transient failure)
      let patchOk = false;
      for (let attempt = 0; attempt < 2 && !patchOk; attempt++) {
        try {
          await api.patch('/users/me/onboarding', {
            wizard_completed: true,
            wizard_completed_at: new Date().toISOString()
          });
          patchOk = true;
        } catch (patchErr) {
          console.warn(`Onboarding PATCH attempt ${attempt + 1} failed:`, patchErr?.response?.status, patchErr?.message);
          if (attempt === 0) await new Promise(r => setTimeout(r, 1000));
        }
      }
      
      // Clear onboarding flags regardless of PATCH result
      // so the user is never stuck in a loop
      localStorage.removeItem('show_onboarding_wizard');
      localStorage.removeItem('new_tenant_client_id');
      
      // Send completion email (async, non-blocking)
      try {
        await api.post('/onboarding/complete-email');
      } catch (e) {
        // Ignore email errors
      }
      
      toast.success('Setup complete! Welcome to EQUIPR CORE');
      
      // Redirect to Getting Started page
      setTimeout(() => {
        navigate('/getting-started');
      }, 1500);
      
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Even on total failure, let the user proceed — the flag can be
      // set retroactively and should not gate dashboard access.
      localStorage.removeItem('show_onboarding_wizard');
      localStorage.removeItem('new_tenant_client_id');
      toast.success('Setup complete! Redirecting...');
      setTimeout(() => {
        navigate('/getting-started');
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  const progressPercent = (currentStepIndex / (STEPS.length - 1)) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="companyName"
                value={companyInfo.companyName}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Your Company Name"
                data-testid="onboarding-company-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type of Company <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(COMPANY_TYPES).map(([key, type]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleCompanyTypeChange(key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      companyInfo.companyType === key 
                        ? 'border-[#ffd108] bg-[#ffd108]/10' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    data-testid={`company-type-${key}`}
                  >
                    <div className="font-medium">{type.name}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {type.assetTypes.length} asset types
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="timezone">Location / Timezone</Label>
                <Select
                  value={companyInfo.timezone}
                  onValueChange={(value) => setCompanyInfo(prev => ({ ...prev, timezone: value }))}
                >
                  <SelectTrigger data-testid="onboarding-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Display Mode</Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyInfo(prev => ({ ...prev, theme: 'light' }));
                      setTheme('light');
                    }}
                    className={`flex-1 p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      companyInfo.theme === 'light' 
                        ? 'border-[#ffd108] bg-[#ffd108]/10 text-[#ffd108]' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <Sun className="h-5 w-5" />
                    Light
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyInfo(prev => ({ ...prev, theme: 'dark' }));
                      setTheme('dark');
                    }}
                    className={`flex-1 p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      companyInfo.theme === 'dark' 
                        ? 'border-[#ffd108] bg-[#ffd108]/10 text-[#ffd108]' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <Moon className="h-5 w-5" />
                    Dark
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 2:
        // Asset types come from the tenant's seeded data config (Data Management → Asset Types)
        const availableTypes = systemAssetTypes;
        
        // Combine seeded and newly-added custom types for selection
        const allAvailableTypes = [...availableTypes, ...customAssetTypes];
          
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Select Asset Types You Have</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleAssetTypeToggle(type)}
                    className={`p-3 rounded-lg border text-sm text-left transition-all ${
                      selectedAssetTypes.includes(type)
                        ? 'border-[#ffd108] bg-[#ffd108]/10 text-[#ffd108]'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {selectedAssetTypes.includes(type) && <Check className="h-4 w-4" />}
                      {type}
                    </div>
                  </button>
                ))}
                
                {/* Custom asset types with remove option */}
                {customAssetTypes.map(type => (
                  <div key={type} className="relative">
                    <button
                      type="button"
                      onClick={() => handleAssetTypeToggle(type)}
                      className={`w-full p-3 rounded-lg border text-sm text-left transition-all ${
                        selectedAssetTypes.includes(type)
                          ? 'border-[#ffd108] bg-[#ffd108]/10 text-[#ffd108]'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-2 pr-6">
                        {selectedAssetTypes.includes(type) && <Check className="h-4 w-4" />}
                        {type}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomAssetType(type);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-destructive"
                      title="Remove custom type"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Add custom asset type */}
              <div className="flex gap-2 mt-4">
                <Input
                  value={newCustomType}
                  onChange={(e) => setNewCustomType(e.target.value)}
                  placeholder="Add custom asset type..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomAssetType();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCustomAssetType}
                  disabled={!newCustomType.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Don't see your asset type? Add a custom one above.
              </p>
            </div>
            
            <div className="space-y-4">
              <Label>Add Your Assets</Label>
              {assets.map((asset, index) => (
                <div key={`step-${index}`} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <Select
                      value={asset.assetType}
                      onValueChange={(value) => updateAsset(index, 'assetType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedAssetTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Input
                      value={asset.assetId}
                      onChange={(e) => updateAsset(index, 'assetId', e.target.value)}
                      placeholder="Asset ID (e.g., CRANE-01)"
                    />
                  </div>
                  {assets.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAsset(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addAsset}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Asset
              </Button>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Add the names of your operators. You can add more details later.
            </p>
            
            <div className="space-y-3">
              {operators.map((op, index) => (
                <div key={`item-${index}`} className="flex gap-3 items-center">
                  <div className="p-2 bg-secondary rounded-lg">
                    <HardHat className="h-5 w-5 text-[#ffd108]" />
                  </div>
                  <Input
                    value={op.name}
                    onChange={(e) => updateOperator(index, e.target.value)}
                    placeholder="Operator name"
                    className="flex-1"
                  />
                  {operators.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOperator(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addOperator}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Operator
              </Button>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Add a customer so you can create your first job in the next step.
            </p>

            <div className="flex gap-3 rounded-lg border border-[#ffd108]/30 bg-[#ffd108]/10 p-4" data-testid="customer-import-note">
              <Info className="h-5 w-5 text-[#ffd108] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground/90 space-y-1">
                <p className="font-medium">Tip: just add one customer for now</p>
                <p className="text-muted-foreground">
                  You only need a single customer to create your first job. The rest are best
                  imported from your accounting system (Xero, MYOB or QuickBooks) after setup,
                  which keeps everything in sync automatically.
                </p>
                <p className="text-muted-foreground">
                  Make sure the customer name spelling here <span className="font-medium text-foreground/90">exactly matches</span> the
                  name in your accounting system so the records link correctly later.
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              {customers.map((cust, index) => (
                <div key={`item-${index}`} className="flex gap-3 items-center">
                  <div className="p-2 bg-secondary rounded-lg">
                    <Building2 className="h-5 w-5 text-[#ffd108]" />
                  </div>
                  <Input
                    value={cust.name}
                    onChange={(e) => updateCustomer(index, e.target.value)}
                    placeholder="Customer/Company name"
                    className="flex-1"
                  />
                  {customers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomer(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addCustomer}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Customer
              </Button>
            </div>
          </div>
        );
        
      case 5:
        // Get available equipment types from the assets created
        const equipmentTypes = [...new Set(createdData.assets.map(a => a.equipment_type))];
        
        return (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Create your first job to see how EQUIPR CORE works. You'll be able to allocate equipment and operators from the Jobs page.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer <span className="text-red-500">*</span></Label>
                <Select
                  value={firstJob.customerId}
                  onValueChange={(value) => setFirstJob(prev => ({ ...prev, customerId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {createdData.customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Asset Type <span className="text-red-500">*</span></Label>
                <Select
                  value={firstJob.equipmentType}
                  onValueChange={(value) => setFirstJob(prev => ({ ...prev, equipmentType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Location <span className="text-red-500">*</span></Label>
                <GooglePlacesAutocomplete
                  value={firstJob.location}
                  onChange={(e) => setFirstJob(prev => ({ ...prev, location: e.target.value }))}
                  onPlaceSelect={(place) => {
                    setFirstJob(prev => ({ 
                      ...prev, 
                      location: place.formatted_address || place.street_address,
                      // Store structured address data for the job
                      locationStreet: place.street_address,
                      locationSuburb: place.suburb,
                      locationState: place.state,
                      locationPostcode: place.postcode
                    }));
                  }}
                  placeholder="Start typing address..."
                  data-testid="onboarding-location-autocomplete"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date <span className="text-red-500">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {firstJob.date 
                        ? format(parse(firstJob.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={firstJob.date ? parse(firstJob.date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setFirstJob(prev => ({ ...prev, date: format(date, 'yyyy-MM-dd') }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>Start Time <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Select
                    value={firstJob.time?.split(':')[0] || '07'}
                    onValueChange={(hour) => setFirstJob(prev => ({ ...prev, time: `${hour}:${prev.time?.split(':')[1] || '00'}` }))}
                  >
                    <SelectTrigger className="w-24 font-mono" data-testid="onboarding-first-job-start-hour">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center text-lg font-bold">:</span>
                  <Select
                    value={firstJob.time?.split(':')[1] || '00'}
                    onValueChange={(minute) => setFirstJob(prev => ({ ...prev, time: `${prev.time?.split(':')[0] || '07'}:${minute}` }))}
                  >
                    <SelectTrigger className="w-24 font-mono" data-testid="onboarding-first-job-start-minute">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                value={firstJob.notes}
                onChange={(e) => setFirstJob(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Brief description of the job (e.g., Crane hire for site lift)"
                rows={3}
                required
              />
            </div>
          </div>
        );
        
      case 6:
        return (
          <div className="text-center py-8">
            <div className="inline-flex p-6 bg-[#ffd108]/20 rounded-full mb-6">
              <PartyPopper className="h-16 w-16 text-[#ffd108]" />
            </div>
            <h2 className="text-3xl font-heading font-bold mb-4">You're All Set!</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
              You've created your first job. Your EQUIPR CORE workspace is ready to go.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
              <div className="p-4 bg-secondary rounded-lg">
                <div className="text-2xl font-bold text-[#ffd108]">{createdData.assets.length}</div>
                <div className="text-sm text-muted-foreground">Assets</div>
              </div>
              <div className="p-4 bg-secondary rounded-lg">
                <div className="text-2xl font-bold text-[#ffd108]">{createdData.operators.length}</div>
                <div className="text-sm text-muted-foreground">Operators</div>
              </div>
              <div className="p-4 bg-secondary rounded-lg">
                <div className="text-2xl font-bold text-[#ffd108]">{createdData.customers.length}</div>
                <div className="text-sm text-muted-foreground">Customers</div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              We'll send you an email with tips to finalize your setup.
            </p>
            
            <Button 
              onClick={handleComplete} 
              className="btn-volt px-8 py-6 text-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Finishing up...
                </>
              ) : (
                <>
                  Go to Dashboard
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
            <span className="font-heading font-bold text-lg text-foreground">EQUIPR CORE</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Step {currentStepIndex + 1} of {STEPS.length}
          </div>
        </div>
      </header>
      
      {/* Progress Bar */}
      <div className="px-6 py-4 border-b border-border">
        <div className="max-w-4xl mx-auto">
          <Progress value={progressPercent} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step) => {
              const isComplete = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              const StepIcon = step.icon;
              
              return (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center ${
                    isCurrent ? 'text-black dark:text-[#ffd108]' : isComplete ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'
                  }`}
                >
                  <div className={`p-2 rounded-full ${
                    isCurrent 
                      ? 'bg-black dark:bg-[#ffd108]/20 ring-2 ring-[#ffd108]' 
                      : isComplete 
                        ? 'bg-green-600/20 dark:bg-green-500/20' 
                        : 'bg-secondary'
                  }`}>
                    {isComplete ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <StepIcon className={`h-4 w-4 ${isCurrent ? 'text-[#ffd108] dark:text-[#ffd108]' : ''}`} />
                    )}
                  </div>
                  <span className="text-xs mt-1 hidden md:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 px-6 py-8">
        <div className="max-w-2xl mx-auto">
          {currentStep < 6 && (
            <div className="mb-8">
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                {STEPS[currentStepIndex]?.title}
              </h1>
            </div>
          )}
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              {renderStepContent()}
            </CardContent>
          </Card>
          
          {/* Navigation */}
          {currentStep < 6 && (
            <div className="flex justify-between mt-6">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 2 || saving}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              <Button
                onClick={handleNext}
                disabled={saving || !validateStep(currentStep)}
                className="btn-volt"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    {currentStep === 5 ? 'Create Job' : 'Next'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
